import React, { useState } from "react";
import { Helmet } from "react-helmet";
import {
  ChevronDown,
  Compass,
  Flame,
  Gauge,
  Mic,
  Scale,
  Shield,
  Users,
} from "lucide-react";
import { APP_STORE_URL, PLAY_STORE_URL } from "../lib/appStores";
import LegalShell from "./LegalShell";
import LatestPatchCard from "./LatestPatchCard";

const ACCENT = "#F5A623";

const LEVELS = [
  { level: 1, weight: "1x", streak: "Starting level", badge: "/images/updates/vote-badge-1.png", weightImg: "/images/updates/vote-weight-1.png" },
  { level: 2, weight: "2x", streak: "4-day streak", badge: "/images/updates/vote-badge-2.png", weightImg: "/images/updates/vote-weight-2.png" },
  { level: 3, weight: "3x", streak: "8-day streak", badge: "/images/updates/vote-badge-3.png", weightImg: "/images/updates/vote-weight-3.png" },
  { level: 4, weight: "4x", streak: "16-day streak", badge: "/images/updates/vote-badge-4.png", weightImg: "/images/updates/vote-weight-4.png" },
  { level: 5, weight: "5x", streak: "64-day streak", badge: "/images/updates/vote-badge-5.png", weightImg: "/images/updates/vote-weight-5.png" },
];

function FlameIcon({ lit, size = 28 }) {
  return (
    <img
      src={lit ? "/images/updates/flame-lit.svg" : "/images/updates/flame-unlit.svg"}
      alt={lit ? "Lit streak flame" : "Unlit streak flame"}
      width={size}
      height={size}
      className="shrink-0"
    />
  );
}

function AppleLogo({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      fill="currentColor"
    >
      <path d="M16.365 12.89c-.025-2.64 2.16-3.91 2.258-3.968-1.232-1.8-3.148-2.047-3.824-2.072-1.626-.166-3.18.958-4.006.958-.842 0-2.126-.94-3.5-.914-1.797.027-3.46 1.046-4.386 2.653-1.876 3.255-.479 8.07 1.346 10.713.896 1.294 1.956 2.745 3.348 2.694 1.348-.054 1.856-.87 3.486-.87 1.616 0 2.088.87 3.51.84 1.454-.026 2.374-1.314 3.258-2.616.99-1.44 1.396-2.84 1.416-2.912-.031-.014-2.72-1.044-2.746-4.14zM14.17 6.26c.74-.9 1.24-2.15 1.104-3.4-1.066.043-2.36.71-3.126 1.61-.686.796-1.29 2.08-1.13 3.306 1.196.093 2.416-.608 3.152-1.516z" />
    </svg>
  );
}

function GooglePlayLogo({ className }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path fill="#34A853" d="M3.06 21.28c.24.22.56.35.9.36l.14-.01 9.16-5.2-2.3-2.3-7.9 7.15z" />
      <path fill="#4285F4" d="M20.54 10.16 17.2 8.26 14.66 10.8l2.3 2.3 3.58-2.03c.7-.4.7-1.47 0-1.91z" />
      <path fill="#FBBC04" d="M3.06 2.72C2.7 3.05 2.5 3.52 2.5 4.02v15.96c0 .5.2.97.56 1.3L13.26 12 3.06 2.72z" />
      <path fill="#EA4335" d="M13.26 12 3.06 2.72c.24-.22.56-.35.9-.36l.14.01 13.1 7.45L13.26 12z" />
    </svg>
  );
}

function PatchStoreLinks() {
  return (
    <div className="border-t border-white/10 pt-6">
      <p className="text-center text-sm font-semibold text-white">
        Get this update
      </p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-white/70 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
        >
          <AppleLogo className="h-4 w-4" />
          App Store
        </a>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-black transition-opacity hover:opacity-90"
          style={{ backgroundColor: ACCENT }}
        >
          <GooglePlayLogo className="h-4 w-4" />
          Google Play
        </a>
      </div>
    </div>
  );
}

function StreakRow({ lit = 3, total = 4 }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/40 px-4 py-3">
      <span className="mr-1 font-semibold tracking-wide text-white/80">Lvl. 1</span>
      <div className="flex flex-1 items-center justify-between gap-1">
        {Array.from({ length: total }, (_, i) => (
          <FlameIcon key={i} lit={i < lit} size={total > 8 ? 18 : 26} />
        ))}
      </div>
    </div>
  );
}

function PatchHeader({ id, dateLabel, isOpen, onToggle, latest = false }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors hover:bg-white/5"
      aria-expanded={isOpen}
    >
      <div>
        <p
          className="text-xs font-bold uppercase tracking-[0.2em]"
          style={{ color: latest ? ACCENT : "rgba(255,255,255,0.4)" }}
        >
          {latest ? "Latest" : "Previous"}
        </p>
        <h2 className="mt-1 text-3xl font-black tracking-tight text-white sm:text-4xl">
          {id}
        </h2>
        <p className="mt-1 text-sm text-white/50">{dateLabel}</p>
      </div>
      <ChevronDown
        className={`h-6 w-6 shrink-0 text-yellow-400 transition-transform duration-200 ${
          isOpen ? "rotate-180" : ""
        }`}
      />
    </button>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className="flex h-10 w-10 items-center justify-center rounded-full"
        style={{ backgroundColor: ACCENT }}
      >
        <Icon className="h-5 w-5 text-black" />
      </span>
      <h3 className="text-2xl font-bold text-white">{children}</h3>
    </div>
  );
}

const WEEKEND_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function WeekendStrip() {
  return (
    <div className="grid grid-cols-7 gap-1.5">
      {WEEKEND_DAYS.map((day) => {
        const live = day === "Sat" || day === "Sun";
        return (
          <div
            key={day}
            className={`rounded-xl border px-1 py-3 text-center ${
              live
                ? "border-yellow-500/40 bg-yellow-500/10"
                : "border-white/10 bg-black/40"
            }`}
          >
            <p
              className={`text-[11px] font-bold uppercase tracking-wider ${
                live ? "text-yellow-400" : "text-white/40"
              }`}
            >
              {day}
            </p>
            <p
              className={`mt-1 text-[10px] font-semibold uppercase tracking-wider ${
                live ? "text-white" : "text-white/30"
              }`}
            >
              {live ? "Live" : "Posts"}
            </p>
          </div>
        );
      })}
    </div>
  );
}

export default function PatchUpdates() {
  const [openIds, setOpenIds] = useState(new Set());

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isOct10Open = openIds.has("26.10.10");
  const isOct1Open = openIds.has("26.10.01");
  const isOpen = openIds.has("26.09.20");

  return (
    <LegalShell
      title="Patch updates"
      subtitle="What's new in the Pagent app"
    >
      <Helmet>
        <title>Patch updates - Pagent</title>
        <meta
          name="description"
          content="The latest Pagent app changes: the Grand Legend and Idolized ranks with daily leaderboard updates and Season 1 details, a new theme every week with songs that carry over, and live dance, karaoke, and rap every weekend."
        />
        <link rel="canonical" href="https://www.pagent.tv/updates" />
      </Helmet>

      <div className="space-y-4">
        <LatestPatchCard defaultOpen />

        {/* 26.10.10 */}
        <article className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/60">
          <PatchHeader
            id="26.10.10"
            dateLabel="October 10, 2026"
            isOpen={isOct10Open}
            onToggle={() => toggle("26.10.10")}
          />

          <div
            className={`grid transition-all duration-200 ${
              isOct10Open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
            }`}
          >
            <div className="overflow-hidden">
              <div className="space-y-8 border-t border-white/10 px-6 pb-8 pt-6">
                <p className="text-lg leading-relaxed text-zinc-300">
                  This patch adds anti-cheating checks and adjusts which
                  pagents voters see, so matchups are fairer and waits are
                  shorter.
                </p>

                {/* Anti-cheating */}
                <section className="space-y-5">
                  <SectionTitle icon={Shield}>Anti-cheating</SectionTitle>

                  <p className="leading-relaxed text-zinc-300">
                    Anti-cheating measures are now in place. Multiple votes and
                    comments toward one idol were detected from a single app
                    install. The ranks that came from that have been reverted,
                    with some removed and some added back. Going forward, doing
                    this is a permanent ban.
                  </p>

                  <img
                    src="/images/updates/patch-26-10-10-anticheat.jpg"
                    alt="Anti-cheat. Using multiple accounts on one install is a permanent ban."
                    className="w-full rounded-2xl border border-white/10"
                  />
                </section>

                {/* Fairer matchups */}
                <section className="space-y-5">
                  <SectionTitle icon={Scale}>Fairer matchups</SectionTitle>

                  <p className="leading-relaxed text-zinc-300">
                    Rank thresholds for which pagents are shown to voters have
                    been adjusted so matchups are fairer and wait times are
                    shorter. I&apos;ll watch this for a week, then adjust again
                    if it needs it.
                  </p>
                  <p className="text-sm text-white/50">Admin</p>

                  <img
                    src="/images/updates/patch-26-10-10-ranks.jpg"
                    alt="Fairer ranks and shorter waits. Watching this for a week."
                    className="w-full rounded-2xl border border-white/10"
                  />
                </section>

                <PatchStoreLinks />
              </div>
            </div>
          </div>
        </article>

        {/* 26.10.01 */}
        <article className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/60">
          <PatchHeader
            id="26.10.01"
            dateLabel="October 1, 2026"
            isOpen={isOct1Open}
            onToggle={() => toggle("26.10.01")}
          />

          <div
            className={`grid transition-all duration-200 ${
              isOct1Open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
            }`}
          >
            <div className="overflow-hidden">
              <div className="space-y-8 border-t border-white/10 px-6 pb-8 pt-6">
                <p className="text-lg leading-relaxed text-zinc-300">
                  This patch brings live Karaoke battles to the weekend, makes
                  groups a permanent part of Pagent, and opens up the Discover
                  page so more idols get seen.
                </p>

                {/* Karaoke live on weekends */}
                <section className="space-y-5">
                  <SectionTitle icon={Mic}>
                    Karaoke goes live on weekends
                  </SectionTitle>

                  <p className="leading-relaxed text-zinc-300">
                    Karaoke is now a live category every weekend. On Saturdays
                    and Sundays, picking a song pairs you with another singer
                    who chose the same track for a real-time 1v1 battle. The instrumental plays, the mic swaps between you
                    line by line, and the community votes on the result.
                  </p>

                  <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
                    <WeekendStrip />
                    <p className="mt-4 text-center text-sm text-white/60">
                      Weekdays keep the recorded karaoke posts you already know.
                      Weekends switch to live battles.
                    </p>
                  </div>
                </section>

                {/* Groups are permanent */}
                <section className="space-y-5">
                  <SectionTitle icon={Users}>
                    Groups are here to stay
                  </SectionTitle>

                  <p className="leading-relaxed text-zinc-300">
                    Groups started as a contest feature. They are now a
                    permanent part of Pagent, and they can build a following of
                    their own. Follow a group the same way you follow an idol
                    and keep up with everything the group posts together.
                  </p>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
                      <p className="text-xs font-bold uppercase tracking-widest text-white/40">
                        Before
                      </p>
                      <p className="mt-2 text-sm leading-relaxed text-zinc-300">
                        Groups existed for the Girl Group contest only. Followers
                        were something idols earned individually.
                      </p>
                    </div>
                    <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/5 p-5">
                      <p
                        className="text-xs font-bold uppercase tracking-widest"
                        style={{ color: ACCENT }}
                      >
                        Now
                      </p>
                      <p className="mt-2 text-sm leading-relaxed text-zinc-300">
                        Groups are permanent and accumulate followers just like
                        idols do. A group&apos;s follower count is its own, separate
                        from each member&apos;s.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-black/30 p-5">
                    <Scale className="mt-0.5 h-5 w-5 shrink-0 text-yellow-400" />
                    <p className="leading-relaxed text-zinc-300">
                      Nothing changes for the Girl Group contest. Being in a
                      group that ranks in the top 50% is still a requirement to
                      pass Mission 2.
                    </p>
                  </div>
                </section>

                {/* Discover */}
                <section className="space-y-5">
                  <SectionTitle icon={Compass}>
                    Discover shows more idols
                  </SectionTitle>

                  <p className="leading-relaxed text-zinc-300">
                    The Discover page now showcases more idols at once, so
                    browsing surfaces a wider range of creators instead of the
                    same handful at the top. Expect to find more new faces every
                    time you open it.
                  </p>
                </section>

                <PatchStoreLinks />
              </div>
            </div>
          </div>
        </article>

        {/* 26.09.20 */}
        <article className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/60">
          <PatchHeader
            id="26.09.20"
            dateLabel="September 20, 2026"
            isOpen={isOpen}
            onToggle={() => toggle("26.09.20")}
          />

          <div
            className={`grid transition-all duration-200 ${
              isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
            }`}
          >
            <div className="overflow-hidden">
              <div className="space-y-8 border-t border-white/10 px-6 pb-8 pt-6">
                <p className="text-lg leading-relaxed text-zinc-300">
                  This patch makes voting power fairer and gives dedicated
                  voters a clearer way to grow their influence, without
                  stacking the board for whoever is already ahead.
                </p>

                {/* Voting power */}
                <section className="space-y-5">
                  <SectionTitle icon={Flame}>
                    Voting power now follows daily streaks
                  </SectionTitle>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
                      <p className="text-xs font-bold uppercase tracking-widest text-white/40">
                        Before
                      </p>
                      <p className="mt-2 text-sm leading-relaxed text-zinc-300">
                        Levels went up when you voted for the leading player.
                        That rewarded piling onto whoever was already winning.
                      </p>
                      <img
                        src="/images/updates/leading.png"
                        alt="LEADING badge from the Pagent app"
                        className="mx-auto mt-5 h-14 w-auto object-contain sm:h-16"
                      />
                    </div>
                    <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/5 p-5">
                      <p
                        className="text-xs font-bold uppercase tracking-widest"
                        style={{ color: ACCENT }}
                      >
                        Now
                      </p>
                      <p className="mt-2 text-sm leading-relaxed text-zinc-300">
                        Vote at least once a day to light a flame. Fill the row
                        to reach the next level. Higher levels make your votes
                        count more.
                      </p>
                      <div className="mt-5">
                        <StreakRow lit={3} total={4} />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-black/30 p-5">
                    <Scale className="mt-0.5 h-5 w-5 shrink-0 text-yellow-400" />
                    <p className="leading-relaxed text-zinc-300">
                      The change is about fairness and dedication. Voting with
                      the leader is no longer how you level up, so underdogs
                      still get an honest shot. Showing up every day is what
                      grows your voting power.
                    </p>
                  </div>

                  <div className="flex items-center justify-center gap-2 py-2">
                    <img
                      src="/images/updates/profile-fire.svg"
                      alt="Pagent streak flame"
                      className="h-10 w-10"
                    />
                    <p className="text-sm text-white/60">
                      Each day you vote lights a flame. Miss a day and the
                      streak restarts, but you keep every level you&apos;ve
                      already earned.
                    </p>
                  </div>

                  <div className="overflow-hidden rounded-2xl border border-white/10">
                    <div className="grid grid-cols-[auto_1fr_1fr] items-center gap-3 bg-white/5 px-4 py-3 text-xs font-bold uppercase tracking-wider text-white/50 sm:px-5">
                      <span>Level</span>
                      <span>Daily streak</span>
                      <span className="text-right">Vote strength</span>
                    </div>
                    {LEVELS.map((row) => (
                      <div
                        key={row.level}
                        className="grid grid-cols-[auto_1fr_1fr] items-center gap-3 border-t border-white/10 px-4 py-3 sm:px-5"
                      >
                        <div className="flex items-center gap-2">
                          <FlameIcon lit size={20} />
                          <span className="font-semibold text-white">
                            Lvl. {row.level}
                          </span>
                        </div>
                        <span className="text-sm text-zinc-300">
                          {row.streak}
                        </span>
                        <div className="flex items-center justify-end gap-2">
                          <img
                            src={row.weightImg}
                            alt={`${row.weight} vote weight`}
                            className="h-7 w-auto object-contain"
                          />
                          <img
                            src={row.badge}
                            alt={`${row.weight} vote badge`}
                            className="hidden h-7 w-auto object-contain sm:block"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <ul className="space-y-2 text-sm text-zinc-300">
                    <li>
                      Only your first vote each day moves the streak. Any
                      vote counts.
                    </li>
                    <li>
                      Accuracy (picking the current leader) is still tracked in
                      your stats. It just no longer decides your level.
                    </li>
                  </ul>
                </section>

                {/* Low performance mode */}
                <section className="space-y-5">
                  <SectionTitle icon={Gauge}>Low performance mode</SectionTitle>

                  <p className="leading-relaxed text-zinc-300">
                    There is now a low performance mode of the app. It replaces
                    looping vote-feed animations with static art so voting stays
                    smooth on older devices. Turn it on or off from{" "}
                    <span className="font-semibold text-white">
                      Voting Preferences
                    </span>
                    .
                  </p>

                  <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
                    <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="font-semibold text-white">
                          Lower performance mode
                        </p>
                        <p className="mt-1 text-sm text-zinc-400">
                          Replaces animations with static art for smoother
                          voting on older devices.
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-center">
                          <img
                            src="/images/updates/off-toggle.svg"
                            alt="Low performance mode off"
                            className="h-8 w-auto"
                          />
                          <p className="mt-1 text-[11px] uppercase tracking-wider text-white/40">
                            Off
                          </p>
                        </div>
                        <div className="text-center">
                          <img
                            src="/images/updates/on-toggle.svg"
                            alt="Low performance mode on"
                            className="h-8 w-auto"
                          />
                          <p className="mt-1 text-[11px] uppercase tracking-wider text-yellow-400/80">
                            On
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <PatchStoreLinks />
              </div>
            </div>
          </div>
        </article>
      </div>
    </LegalShell>
  );
}
